import {
  ALLOWED_ASSET_URLS,
  ValidationFinding,
} from "./emailTemplateValidation.types"

const parseHtml = (html: string): Document =>
  new DOMParser().parseFromString(html, "text/html")

const isHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url)

// ---------------------------------------------------------------------------
// Rule 1 — Pebble variables
// ---------------------------------------------------------------------------

// Keywords, operators, tests and literals that are never external variables.
const PEBBLE_RESERVED = new Set([
  "and",
  "or",
  "not",
  "in",
  "is",
  "if",
  "else",
  "elseif",
  "endif",
  "for",
  "endfor",
  "set",
  "endset",
  "unless",
  "endunless",
  "true",
  "false",
  "none",
  "null",
  "empty",
  "defined",
  "even",
  "odd",
  "iterable",
  "starts",
  "ends",
  "with",
  "matches",
  "contains",
  "loop",
])

// Extract variable reference paths (e.g. `user.name`) from a raw Pebble
// expression, dropping string literals, filters, function calls and keywords.
const referencesInExpression = (expression: string): string[] => {
  const withoutStrings = expression
    .replace(/'[^']*'/g, " ")
    .replace(/"[^"]*"/g, " ")
  // Drop the identifier that names a filter (`| upper`, `| date(...)`).
  const withoutFilters = withoutStrings.replace(/\|\s*[a-zA-Z_]\w*/g, " ")

  const references: string[] = []
  const pattern = /([a-zA-Z_]\w*(?:\.[a-zA-Z_]\w*)*)(\s*\()?/g
  let match: RegExpExecArray | null
  while ((match = pattern.exec(withoutFilters)) !== null) {
    const path = match[1]
    const isFunctionCall = !!match[2] && !path.includes(".")
    const base = path.split(".")[0]
    if (isFunctionCall) continue
    if (/^\d/.test(base)) continue
    if (PEBBLE_RESERVED.has(base)) continue
    references.push(path)
  }
  return references
}

interface PebbleScan {
  references: string[]
  locals: string[]
}

const scanStatementTag = (inner: string): PebbleScan => {
  const references: string[] = []
  const locals: string[] = []

  const forMatch =
    /^for\s+([a-zA-Z_]\w*(?:\s*,\s*[a-zA-Z_]\w*)*)\s+in\s+(\S[\s\S]*)$/.exec(
      inner,
    )
  if (forMatch) {
    locals.push(...forMatch[1].split(",").map((name) => name.trim()))
    references.push(...referencesInExpression(forMatch[2]))
    return { references, locals }
  }

  const setMatch = /^set\s+([a-zA-Z_]\w*)\s*=\s*(\S[\s\S]*)$/.exec(inner)
  if (setMatch) {
    locals.push(setMatch[1])
    references.push(...referencesInExpression(setMatch[2]))
    return { references, locals }
  }

  const conditionalMatch = /^(if|elseif|unless)\s+(\S[\s\S]*)$/.exec(inner)
  if (conditionalMatch) {
    references.push(...referencesInExpression(conditionalMatch[2]))
  }

  return { references, locals }
}

export const extractVariables = (html: string, plain: string): string[] => {
  const source = `${html}\n${plain}`
  const references: string[] = []
  const locals = new Set<string>()

  const statementPattern = /\{%([\s\S]+?)%\}/g
  let statement: RegExpExecArray | null
  while ((statement = statementPattern.exec(source)) !== null) {
    const scan = scanStatementTag(statement[1].trim())
    references.push(...scan.references)
    scan.locals.forEach((name) => locals.add(name))
  }

  const outputPattern = /\{\{([^{}]+)\}\}/g
  let output: RegExpExecArray | null
  while ((output = outputPattern.exec(source)) !== null) {
    references.push(...referencesInExpression(output[1].trim()))
  }

  const external = references.filter((path) => !locals.has(path.split(".")[0]))
  return [...new Set(external)].sort((a, b) => a.localeCompare(b))
}

// ---------------------------------------------------------------------------
// Assets & links (URL collection — shared by static and network rules)
// ---------------------------------------------------------------------------

export const extractAssetUrls = (html: string): string[] => {
  const doc = parseHtml(html)
  const urls: string[] = []

  doc.querySelectorAll("img[src]").forEach((img) => {
    urls.push(img.getAttribute("src") ?? "")
  })
  doc.querySelectorAll("[background]").forEach((element) => {
    urls.push(element.getAttribute("background") ?? "")
  })

  const cssSources = [
    ...Array.from(doc.querySelectorAll("[style]")).map(
      (element) => element.getAttribute("style") ?? "",
    ),
    ...Array.from(doc.querySelectorAll("style")).map(
      (element) => element.textContent ?? "",
    ),
  ]
  const urlPattern = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^'")\s]+))\s*\)/gi
  cssSources.forEach((css) => {
    let match: RegExpExecArray | null
    while ((match = urlPattern.exec(css)) !== null) {
      urls.push((match[1] ?? match[2] ?? match[3]).trim())
    }
  })

  // Relative and other non-http sources are kept: they are broken assets, not
  // absent ones, and only surface as findings if they reach the asset checks.
  return [...new Set(urls.map((url) => url.trim()).filter(Boolean))]
}

export const extractLinkUrls = (html: string, plain: string): string[] => {
  const doc = parseHtml(html)
  const urls = Array.from(doc.querySelectorAll("a[href]")).map(
    (anchor) => anchor.getAttribute("href") ?? "",
  )

  const bareUrlPattern = /https?:\/\/[^\s"'<>)]+/gi
  urls.push(...(plain.match(bareUrlPattern) ?? []))

  return [...new Set(urls.filter(isHttpUrl))]
}

// ---------------------------------------------------------------------------
// Rule 3 — Assets served from an approved So Energy bucket
// ---------------------------------------------------------------------------

export const isAbsoluteAssetUrl = (url: string): boolean => isHttpUrl(url)

export const isInlineAssetUrl = (url: string): boolean =>
  /^(data|cid):/i.test(url.trim())

export const isAllowedAssetUrl = (url: string): boolean =>
  ALLOWED_ASSET_URLS.some((prefix) => url.startsWith(prefix))

// ---------------------------------------------------------------------------
// Rule 5 — No scripts
// ---------------------------------------------------------------------------

export const findScriptFindings = (html: string): ValidationFinding[] => {
  const doc = parseHtml(html)
  const findings: ValidationFinding[] = []

  doc.querySelectorAll("script").forEach(() => {
    findings.push({
      rule: "script-tags",
      severity: "error",
      message: "Template contains a <script> tag",
    })
  })

  doc.querySelectorAll("*").forEach((element) => {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase()
      const value = attribute.value.trim().toLowerCase()
      if (name.startsWith("on")) {
        findings.push({
          rule: "script-tags",
          severity: "error",
          message: "Template contains an inline event handler",
          detail: `<${element.tagName.toLowerCase()} ${name}>`,
        })
      } else if (value.startsWith("javascript:")) {
        findings.push({
          rule: "script-tags",
          severity: "error",
          message: "Template contains a javascript: URL",
          detail: `${name}="${attribute.value}"`,
        })
      }
    }
  })

  return findings
}

// ---------------------------------------------------------------------------
// Rule 6 — Browser-extension artifacts
// ---------------------------------------------------------------------------

const EXTENSION_ATTR_PREFIXES: { name: string; prefixes: string[] }[] = [
  { name: "Grammarly", prefixes: ["data-gramm", "data-gr-", "data-new-gr-"] },
  { name: "Vercel", prefixes: ["data-vercel"] },
  { name: "LastPass", prefixes: ["data-lastpass", "data-lpignore"] },
  { name: "1Password", prefixes: ["data-1p-", "data-onepassword"] },
  { name: "LanguageTool", prefixes: ["data-lt-"] },
  { name: "ColorZilla", prefixes: ["cz-shortcut-listen"] },
]

const EXTENSION_TAGS: { name: string; tags: string[] }[] = [
  {
    name: "Grammarly",
    tags: ["grammarly-desktop-integration", "grammarly-extension"],
  },
  { name: "Vercel", tags: ["vercel-live-feedback"] },
]

export const findExtensionArtifacts = (html: string): ValidationFinding[] => {
  const doc = parseHtml(html)
  const detected = new Map<string, string>()

  doc.querySelectorAll("*").forEach((element) => {
    const tag = element.tagName.toLowerCase()
    EXTENSION_TAGS.forEach(({ name, tags }) => {
      if (tags.includes(tag)) detected.set(name, `<${tag}>`)
    })
    for (const attribute of Array.from(element.attributes)) {
      const attributeName = attribute.name.toLowerCase()
      EXTENSION_ATTR_PREFIXES.forEach(({ name, prefixes }) => {
        if (prefixes.some((prefix) => attributeName.startsWith(prefix))) {
          detected.set(name, attributeName)
        }
      })
    }
  })

  return [...detected.entries()].map(([name, detail]) => ({
    rule: "extension-artifacts",
    severity: "error",
    message: `${name} browser-extension markup detected`,
    detail,
  }))
}

// ---------------------------------------------------------------------------
// Rule 7 — Footer copyright year, if present, is current
// ---------------------------------------------------------------------------

// The footer is optional, so an absent copyright line is fine. Only flag when
// a "<year> So Energy" line is present but names a year other than the current
// one (e.g. a template carried over from a previous year).
export const checkFooterYear = (html: string): ValidationFinding[] => {
  const currentYear = new Date().getFullYear()
  const pattern = /\b(\d{4})\s+So\s+Energy\b/gi
  const staleYears = new Set<number>()
  let match: RegExpExecArray | null
  while ((match = pattern.exec(html)) !== null) {
    const year = Number(match[1])
    if (year !== currentYear) staleYears.add(year)
  }
  if (!staleYears.size) return []
  return [
    {
      rule: "footer-year",
      severity: "error",
      message: `Footer copyright year is out of date (expected "${currentYear} So Energy")`,
      detail: [...staleYears].sort((a, b) => a - b).join(", "),
    },
  ]
}
